import React from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Alert
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  DataUsage as DataUsageIcon,
  School as SchoolIcon,
  People as PeopleIcon,
  AttachMoney as MoneyIcon,
  ShoppingCart as ShoppingCartIcon,
  Build as BuildIcon
} from '@mui/icons-material';

const DataInspector = () => {
  // Get all Redux data
  const students = useSelector(state => state.students) || [];
  const teachers = useSelector(state => state.teachers) || [];
  const expenses = useSelector(state => state.expenses) || [];
  const sales = useSelector(state => state.sales?.sales) || [];
  const assets = useSelector(state => state.assets?.assets) || [];
  const schooldetails = useSelector(state => state.schooldetails) || [];
  const classData = useSelector(state => state.class) || [];

  // Analyze data structure
  const analyzeData = (data, name) => {
    if (!data || data.length === 0) {
      return {
        count: 0,
        hasData: false,
        sample: null,
        fields: []
      };
    }

    const sample = data[0];
    const fields = sample ? Object.keys(sample) : [];
    
    return {
      count: data.length,
      hasData: true,
      sample: sample,
      fields: fields
    };
  };

  const studentsAnalysis = analyzeData(students, 'students');
  const teachersAnalysis = analyzeData(teachers, 'teachers');
  const expensesAnalysis = analyzeData(expenses, 'expenses');
  const salesAnalysis = analyzeData(sales, 'sales');
  const assetsAnalysis = analyzeData(assets, 'assets');
  const schoolAnalysis = analyzeData(schooldetails, 'schools');
  const classAnalysis = analyzeData(classData, 'classes');

  const totalDataCount = studentsAnalysis.count + teachersAnalysis.count + 
                        expensesAnalysis.count + salesAnalysis.count + 
                        assetsAnalysis.count;

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <DataUsageIcon />
        Data Inspector
      </Typography>
      
      <Alert severity={totalDataCount > 0 ? "success" : "warning"} sx={{ mb: 2 }}>
        {totalDataCount > 0 
          ? `Found ${totalDataCount} total records across all data sources`
          : "No data found in Redux state. Please ensure data is loaded."
        }
      </Alert>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SchoolIcon />
            <Typography>Students ({studentsAnalysis.count})</Typography>
            <Chip 
              label={studentsAnalysis.hasData ? "Has Data" : "No Data"} 
              color={studentsAnalysis.hasData ? "success" : "error"}
              size="small"
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {studentsAnalysis.hasData ? (
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Fields: {studentsAnalysis.fields.join(', ')}
              </Typography>
              <Typography variant="body2" component="pre" sx={{ 
                backgroundColor: '#f5f5f5', 
                p: 1, 
                borderRadius: 1,
                fontSize: '0.75rem',
                overflow: 'auto',
                maxHeight: 200
              }}>
                {JSON.stringify(studentsAnalysis.sample, null, 2)}
              </Typography>
            </Box>
          ) : (
            <Typography color="text.secondary">No student data found</Typography>
          )}
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PeopleIcon />
            <Typography>Teachers ({teachersAnalysis.count})</Typography>
            <Chip 
              label={teachersAnalysis.hasData ? "Has Data" : "No Data"} 
              color={teachersAnalysis.hasData ? "success" : "error"}
              size="small"
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {teachersAnalysis.hasData ? (
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Fields: {teachersAnalysis.fields.join(', ')}
              </Typography>
              <Typography variant="body2" component="pre" sx={{ 
                backgroundColor: '#f5f5f5', 
                p: 1, 
                borderRadius: 1,
                fontSize: '0.75rem',
                overflow: 'auto',
                maxHeight: 200
              }}>
                {JSON.stringify(teachersAnalysis.sample, null, 2)}
              </Typography>
            </Box>
          ) : (
            <Typography color="text.secondary">No teacher data found</Typography>
          )}
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MoneyIcon />
            <Typography>Expenses ({expensesAnalysis.count})</Typography>
            <Chip 
              label={expensesAnalysis.hasData ? "Has Data" : "No Data"} 
              color={expensesAnalysis.hasData ? "success" : "error"}
              size="small"
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {expensesAnalysis.hasData ? (
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Fields: {expensesAnalysis.fields.join(', ')}
              </Typography>
              <Typography variant="body2" component="pre" sx={{ 
                backgroundColor: '#f5f5f5', 
                p: 1, 
                borderRadius: 1,
                fontSize: '0.75rem',
                overflow: 'auto',
                maxHeight: 200
              }}>
                {JSON.stringify(expensesAnalysis.sample, null, 2)}
              </Typography>
            </Box>
          ) : (
            <Typography color="text.secondary">No expense data found</Typography>
          )}
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ShoppingCartIcon />
            <Typography>Sales ({salesAnalysis.count})</Typography>
            <Chip 
              label={salesAnalysis.hasData ? "Has Data" : "No Data"} 
              color={salesAnalysis.hasData ? "success" : "error"}
              size="small"
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {salesAnalysis.hasData ? (
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Fields: {salesAnalysis.fields.join(', ')}
              </Typography>
              <Typography variant="body2" component="pre" sx={{ 
                backgroundColor: '#f5f5f5', 
                p: 1, 
                borderRadius: 1,
                fontSize: '0.75rem',
                overflow: 'auto',
                maxHeight: 200
              }}>
                {JSON.stringify(salesAnalysis.sample, null, 2)}
              </Typography>
            </Box>
          ) : (
            <Typography color="text.secondary">No sales data found</Typography>
          )}
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BuildIcon />
            <Typography>Assets ({assetsAnalysis.count})</Typography>
            <Chip 
              label={assetsAnalysis.hasData ? "Has Data" : "No Data"} 
              color={assetsAnalysis.hasData ? "success" : "error"}
              size="small"
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {assetsAnalysis.hasData ? (
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Fields: {assetsAnalysis.fields.join(', ')}
              </Typography>
              <Typography variant="body2" component="pre" sx={{ 
                backgroundColor: '#f5f5f5', 
                p: 1, 
                borderRadius: 1,
                fontSize: '0.75rem',
                overflow: 'auto',
                maxHeight: 200
              }}>
                {JSON.stringify(assetsAnalysis.sample, null, 2)}
              </Typography>
            </Box>
          ) : (
            <Typography color="text.secondary">No asset data found</Typography>
          )}
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SchoolIcon />
            <Typography>School Details ({schoolAnalysis.count})</Typography>
            <Chip 
              label={schoolAnalysis.hasData ? "Has Data" : "No Data"} 
              color={schoolAnalysis.hasData ? "success" : "error"}
              size="small"
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {schoolAnalysis.hasData ? (
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Fields: {schoolAnalysis.fields.join(', ')}
              </Typography>
              <Typography variant="body2" component="pre" sx={{ 
                backgroundColor: '#f5f5f5', 
                p: 1, 
                borderRadius: 1,
                fontSize: '0.75rem',
                overflow: 'auto',
                maxHeight: 200
              }}>
                {JSON.stringify(schoolAnalysis.sample, null, 2)}
              </Typography>
            </Box>
          ) : (
            <Typography color="text.secondary">No school data found</Typography>
          )}
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SchoolIcon />
            <Typography>Classes ({classAnalysis.count})</Typography>
            <Chip 
              label={classAnalysis.hasData ? "Has Data" : "No Data"} 
              color={classAnalysis.hasData ? "success" : "error"}
              size="small"
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {classAnalysis.hasData ? (
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Fields: {classAnalysis.fields.join(', ')}
              </Typography>
              <Typography variant="body2" component="pre" sx={{ 
                backgroundColor: '#f5f5f5', 
                p: 1, 
                borderRadius: 1,
                fontSize: '0.75rem',
                overflow: 'auto',
                maxHeight: 200
              }}>
                {JSON.stringify(classAnalysis.sample, null, 2)}
              </Typography>
            </Box>
          ) : (
            <Typography color="text.secondary">No class data found</Typography>
          )}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default DataInspector;
